import { Controller } from "@hotwired/stimulus"
import { createIcons, icons } from 'lucide'

export default class extends Controller {
  static targets = [
    "startButton",
    "header",
    "filters",
    "pageBackground",
    "typeChooser",
    "genreChooser",
    "genreCard",
    "genreCheck",
    "genreCount",
    "genreFields",
    "audienceChooser",
    "audienceOption",
    "audienceField",
    "formFields",
    "form",
    "kindField",
    "titleField",
    "screenplayField",
    "novelField",
    "novelPageCount",
    "novelWordCount",
    "commitmentSlider",
    "commitmentValue",
    "commitmentLabel",
    "commitmentIcon",
    "genderOption",
    "projectsList"
  ]

  // Average words per page for novels (industry standard is 250-300)
  static WORDS_PER_PAGE = 275

  static values = {
    selectedGender: { type: String, default: "neutral" },
    selectedGenres: { type: Array, default: [] },
    selectedAudiences: { type: Array, default: [] }
  }

  commitmentLevels = [
    { value: "conservative", label: "Conservative", description: "2 hours or less daily", icon: "coffee" },
    { value: "normal", label: "Normal", description: "3-4 hours daily", icon: "clock" },
    { value: "aggressive", label: "Aggressive", description: "5-8 hours daily", icon: "flame" }
  ]

  connect() {
    this.selectedGenresValue = []
    setTimeout(() => createIcons({ icons }), 0)
  }

  showTypeChooser() {
    this.startButtonTarget.classList.add("hidden")
    this.typeChooserTarget.classList.remove("hidden")
    // Hide header, filters, and projects list when starting the form
    if (this.hasHeaderTarget) {
      this.headerTarget.classList.add("hidden")
    }
    if (this.hasFiltersTarget) {
      this.filtersTarget.classList.add("hidden")
    }
    if (this.hasProjectsListTarget) {
      this.projectsListTarget.classList.add("hidden")
    }
    // Add gradient background
    if (this.hasPageBackgroundTarget) {
      this.pageBackgroundTarget.classList.add("bg-gradient-to-b", "from-primary-50", "to-white")
    }
    setTimeout(() => createIcons({ icons }), 0)
  }

  hideTypeChooser() {
    this.typeChooserTarget.classList.add("hidden")
    this.startButtonTarget.classList.remove("hidden")
    // Show header, filters, and projects list again when canceling
    if (this.hasHeaderTarget) {
      this.headerTarget.classList.remove("hidden")
    }
    if (this.hasFiltersTarget) {
      this.filtersTarget.classList.remove("hidden")
    }
    if (this.hasProjectsListTarget) {
      this.projectsListTarget.classList.remove("hidden")
    }
    // Remove gradient background
    if (this.hasPageBackgroundTarget) {
      this.pageBackgroundTarget.classList.remove("bg-gradient-to-b", "from-primary-50", "to-white")
    }
  }

  selectType(event) {
    const type = event.currentTarget.dataset.type
    this.kindFieldTarget.value = type

    // Hide type chooser, show genre chooser
    this.typeChooserTarget.classList.add("hidden")
    this.genreChooserTarget.classList.remove("hidden")

    setTimeout(() => createIcons({ icons }), 0)
  }

  // Genre selection methods
  toggleGenre(event) {
    const card = event.currentTarget
    const genreId = parseInt(card.dataset.genreId)
    const isSelected = this.selectedGenresValue.includes(genreId)

    if (isSelected) {
      // Deselect
      this.selectedGenresValue = this.selectedGenresValue.filter(id => id !== genreId)
      this.updateGenreCardState(card, false)
    } else if (this.selectedGenresValue.length < 2) {
      // Select (if under limit)
      this.selectedGenresValue = [...this.selectedGenresValue, genreId]
      this.updateGenreCardState(card, true)
    }

    this.updateGenreCount()
  }

  updateGenreCardState(card, isSelected) {
    const checkIcon = card.querySelector('[data-project-form-target="genreCheck"]')

    if (isSelected) {
      card.classList.add("ring-2", "ring-lime-500", "ring-offset-2")
      if (checkIcon) checkIcon.classList.remove("hidden")
    } else {
      card.classList.remove("ring-2", "ring-lime-500", "ring-offset-2")
      if (checkIcon) checkIcon.classList.add("hidden")
    }

    setTimeout(() => createIcons({ icons }), 0)
  }

  updateGenreCount() {
    if (this.hasGenreCountTarget) {
      this.genreCountTarget.textContent = this.selectedGenresValue.length
    }
  }

  skipGenres() {
    this.selectedGenresValue = []
    this.showAudienceChooser()
  }

  continueFromGenres() {
    this.showAudienceChooser()
  }

  showAudienceChooser() {
    // Add hidden fields for selected genres
    this.updateGenreHiddenFields()

    // Hide genre chooser, show audience chooser
    this.genreChooserTarget.classList.add("hidden")
    this.audienceChooserTarget.classList.remove("hidden")

    setTimeout(() => createIcons({ icons }), 0)
  }

  selectAudience(event) {
    const audience = event.currentTarget.dataset.audience
    const isSelected = this.selectedAudiencesValue.includes(audience)

    if (isSelected) {
      // Deselect
      this.selectedAudiencesValue = this.selectedAudiencesValue.filter(a => a !== audience)
      this.updateAudienceCardState(event.currentTarget, false, audience)
    } else {
      // Select
      this.selectedAudiencesValue = [...this.selectedAudiencesValue, audience]
      this.updateAudienceCardState(event.currentTarget, true, audience)
    }
  }

  updateAudienceCardState(card, isSelected, audience) {
    const ringColors = {
      young_male: "ring-blue-500",
      young_female: "ring-pink-500",
      older_male: "ring-indigo-500",
      older_female: "ring-rose-500"
    }

    if (isSelected) {
      card.classList.add("ring-2", "ring-offset-2", ringColors[audience])
    } else {
      card.classList.remove("ring-2", "ring-offset-2", ...Object.values(ringColors))
    }
  }

  continueFromAudience() {
    this.updateAudienceHiddenFields()
    this.showFormFields()
  }

  updateAudienceHiddenFields() {
    if (!this.hasAudienceFieldTarget) return

    // Store as comma-separated values
    this.audienceFieldTarget.value = this.selectedAudiencesValue.join(",")
  }

  skipAudience() {
    this.selectedAudiencesValue = []
    if (this.hasAudienceFieldTarget) {
      this.audienceFieldTarget.value = ""
    }
    this.showFormFields()
  }

  backToAudience() {
    // Hide form, show audience chooser
    this.formFieldsTarget.classList.add("hidden")
    this.audienceChooserTarget.classList.remove("hidden")

    setTimeout(() => createIcons({ icons }), 0)
  }

  showFormFields() {
    // Show/hide appropriate count field based on project type
    const type = this.kindFieldTarget.value
    if (type === "screenplay") {
      this.screenplayFieldTarget.classList.remove("hidden")
      this.novelFieldTarget.classList.add("hidden")
    } else {
      this.screenplayFieldTarget.classList.add("hidden")
      this.novelFieldTarget.classList.remove("hidden")
    }

    // Pre-fill title based on selected genres
    if (this.hasTitleFieldTarget) {
      const genreNames = this.getSelectedGenreNames()
      if (genreNames.length > 0) {
        this.titleFieldTarget.value = `Untitled ${genreNames.join("-")}`
      } else {
        this.titleFieldTarget.value = "Untitled"
      }
    }

    // Hide audience chooser, show form
    this.audienceChooserTarget.classList.add("hidden")
    this.formFieldsTarget.classList.remove("hidden")

    setTimeout(() => createIcons({ icons }), 0)
  }

  getSelectedGenreNames() {
    const names = []
    this.genreCardTargets.forEach(card => {
      const genreId = parseInt(card.dataset.genreId)
      if (this.selectedGenresValue.includes(genreId)) {
        // Get the genre name from the card's text content
        const nameElement = card.querySelector("span.text-xs")
        if (nameElement) {
          names.push(nameElement.textContent.trim())
        }
      }
    })
    return names
  }

  updateGenreHiddenFields() {
    if (!this.hasGenreFieldsTarget) return

    // Clear existing hidden fields
    this.genreFieldsTarget.innerHTML = ''

    // Add hidden fields for each selected genre
    this.selectedGenresValue.forEach(genreId => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'project[genre_ids][]'
      input.value = genreId
      this.genreFieldsTarget.appendChild(input)
    })
  }

  backToType() {
    // Reset genre selections
    this.selectedGenresValue = []
    if (this.hasGenreCardTarget) {
      this.genreCardTargets.forEach(card => {
        this.updateGenreCardState(card, false)
      })
    }
    this.updateGenreCount()

    // Hide genre chooser, show type chooser
    this.genreChooserTarget.classList.add("hidden")
    this.typeChooserTarget.classList.remove("hidden")

    setTimeout(() => createIcons({ icons }), 0)
  }

  backToGenres() {
    // Hide audience chooser, show genre chooser
    this.audienceChooserTarget.classList.add("hidden")
    this.genreChooserTarget.classList.remove("hidden")

    setTimeout(() => createIcons({ icons }), 0)
  }

  // Keep the old goBack for compatibility - now goes to audience
  goBack() {
    this.backToAudience()
  }

  updateCommitment(event) {
    const index = parseInt(event.target.value)
    const level = this.commitmentLevels[index]

    this.commitmentValueTarget.value = level.value
    this.commitmentLabelTarget.textContent = `${level.label} - ${level.description}`

    // Update colors based on level
    const colorClasses = {
      conservative: { bg: "bg-lime-100", text: "text-lime-700", icon: "text-lime-600" },
      normal: { bg: "bg-sky-100", text: "text-sky-700", icon: "text-sky-600" },
      aggressive: { bg: "bg-orange-100", text: "text-orange-700", icon: "text-orange-600" }
    }

    // Remove old color classes
    this.commitmentLabelTarget.classList.remove(
      "bg-lime-100", "text-lime-700",
      "bg-sky-100", "text-sky-700",
      "bg-orange-100", "text-orange-700",
      "bg-primary-100", "text-primary-700"
    )

    // Add new color classes
    const colors = colorClasses[level.value]
    this.commitmentLabelTarget.classList.add(colors.bg, colors.text)

    // Update icon color
    this.commitmentIconTarget.classList.remove("text-lime-600", "text-sky-600", "text-orange-600", "text-primary-600")
    this.commitmentIconTarget.classList.add(colors.icon)

    // Update icon
    this.commitmentIconTarget.setAttribute("data-lucide", level.icon)
    createIcons({ icons, nodes: [this.commitmentIconTarget] })
  }

  selectGender(event) {
    const gender = event.currentTarget.dataset.gender
    this.selectedGenderValue = gender

    // Update hidden field
    const hiddenField = this.element.querySelector('input[name="project[hero_gender]"]')
    if (hiddenField) {
      hiddenField.value = gender
    }

    // Update visual state
    this.genderOptionTargets.forEach(option => {
      const isSelected = option.dataset.gender === gender
      if (isSelected) {
        option.classList.add("border-primary-500", "bg-primary-50")
        option.classList.remove("border-gray-200")
      } else {
        option.classList.remove("border-primary-500", "bg-primary-50")
        option.classList.add("border-gray-200")
      }
    })
  }

  // Simple toggle for edit form
  updateKind(event) {
    const kind = event.target.value
    if (kind === "screenplay") {
      if (this.hasScreenplayFieldTarget) this.screenplayFieldTarget.classList.remove("hidden")
      if (this.hasNovelFieldTarget) this.novelFieldTarget.classList.add("hidden")
    } else {
      if (this.hasScreenplayFieldTarget) this.screenplayFieldTarget.classList.add("hidden")
      if (this.hasNovelFieldTarget) this.novelFieldTarget.classList.remove("hidden")
    }
  }

  // Novel page/word count synchronization
  updateWordCountFromPages(event) {
    const pages = parseInt(event.target.value) || 0
    if (pages > 0 && this.hasNovelWordCountTarget) {
      const words = Math.round(pages * this.constructor.WORDS_PER_PAGE)
      this.novelWordCountTarget.value = words
    }
  }

  updatePageCountFromWords(event) {
    const words = parseInt(event.target.value) || 0
    if (words > 0 && this.hasNovelPageCountTarget) {
      const pages = Math.round(words / this.constructor.WORDS_PER_PAGE)
      this.novelPageCountTarget.value = pages
    }
  }
}
